import { FC, useEffect, useMemo } from 'react';
import { format, formatInTimeZone } from 'date-fns-tz';
import { ColumnDataType, OperationType } from '@webthatmatters/orca-table';
import { NumberFormatValues } from 'react-number-format';
import { strToNumber } from '@/library/utils/numbers';
import { getNumericDaysFilterValue } from '@/common/components/table/utils/filterValue';
import SingleDatePicker, {
  DateInputType,
  DateRangeOptionType,
  DateRangePicker,
  getPastDateRangeOptions
} from '@webthatmatters/orca-dateinput';
import { singleDateField, singleDateTimeField } from '@/common/utils/form/fieldTypes';
import styled from '@emotion/styled';
import variables from '@/library/assets/scss/abstracts/_exports.module.scss';
import NumberInput from '@/library/components/form/inputs/NumberInput';
import usePrevious from '@/library/utils/hooks/usePrevious';
import moment from 'moment';

type RangeValue = string[] | null;
type SingleValue = string | null;

export type ChangedDateFilter = string | string[] | number | null;

type DateFilterSelectorProps = {
  value: string | string[] | null;
  onChange: (date: ChangedDateFilter) => void;
  filter: { value: unknown; column: { type: ColumnDataType }; operation: OperationType };
  options?: DateRangeOptionType[];
  isClearable?: boolean;
};

const dateFormat = 'yyyy-MM-dd';
const datetimeFormat = 'yyyy-MM-dd HH:mm';

// The defaultFilterValues found in some Tables is NOT supported yet
const DateFilterSelector: FC<DateFilterSelectorProps> = ({
  value,
  onChange,
  filter,
  options,
  isClearable = false
}) => {
  const previousFilterOperation = usePrevious(filter.operation.value);

  const selectedNumericValue = useMemo(() => {
    if (['lastDays', 'nextDays'].includes(filter.operation.value)) {
      if (Array.isArray(value)) return value[0] as SingleValue;
    } else if (
      filter.operation.value !== 'between' &&
      !Array.isArray(value) &&
      value !== null &&
      !isNaN(+value)
    ) {
      return value as SingleValue;
    }

    return null;
  }, [filter.operation.value, value]);

  const selectedDateRangeValue = useMemo(() => {
    if (filter.operation.value === 'between' && Array.isArray(value)) {
      return value as RangeValue;
    }

    return null;
  }, [filter.operation.value, value]);

  const selectedSingeDateValue = useMemo(() => {
    if (
      filter.operation.value !== 'between' &&
      !Array.isArray(value) &&
      selectedNumericValue === null
    ) {
      return value as SingleValue;
    }

    return null;
  }, [filter.operation.value, value, selectedNumericValue]);

  const hasTime = ['datetime'].includes(filter.column.type);
  const isUtcTime = ['utc_datetime'].includes(filter.column.type);

  const getFormattedDate = (date: Date) => {
    if (isUtcTime) {
      const dateInUtc = new Date(
        Date.UTC(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          date.getUTCHours(),
          date.getUTCMinutes(),
          date.getUTCSeconds()
        )
      );

      dateInUtc.toISOString();
    }

    if (hasTime) {
      return formatInTimeZone(date, 'UTC', datetimeFormat);
    }

    return format(date, dateFormat);
  };

  useEffect(() => {
    if (
      (filter.operation.value as any) &&
      previousFilterOperation &&
      previousFilterOperation !== filter.operation.value
    ) {
      onChange(null);
    }
  }, [filter.operation.value, onChange, previousFilterOperation]);

  if (!(filter.operation.value as any)) return null; // Fallback for undefined operation

  return (
    <FilterContainer data-type="datefilter" data-operation={filter.operation.value}>
      {filter.operation.value === 'between' ? (
        <RangeDateFilter
          from={
            selectedDateRangeValue?.[0]
              ? hasTime
                ? singleDateTimeField().transformOnLoad(selectedDateRangeValue[0])
                : singleDateField().transformOnLoad(selectedDateRangeValue[0])
              : null
          }
          to={
            selectedDateRangeValue?.[1]
              ? hasTime
                ? singleDateTimeField().transformOnLoad(selectedDateRangeValue[1])
                : singleDateField().transformOnLoad(selectedDateRangeValue[1])
              : null
          }
          onChange={({ from, to }: { from: DateInputType; to: DateInputType }) =>
            onChange(
              from && to ? ([getFormattedDate(from), getFormattedDate(to)] as string[]) : null
            )
          }
          hasTime={hasTime}
          options={options}
          isClearable={isClearable}
        />
      ) : ['lastDays', 'nextDays'].includes(filter.operation.value) ? (
        <NumberInput
          placeholder="No. of days"
          value={selectedNumericValue || ''}
          onChange={e => {
            const numValue = strToNumber(e.target.value);
            onChange(numValue !== null ? [numValue.toString(), moment.tz.guess()] : null);
          }}
          isAllowed={(val: NumberFormatValues) =>
            val.value === '' || (val.floatValue !== undefined && val.floatValue > 0)
          }
          className="cps-12 h-100p"
          invisible
        />
      ) : ['beforeDaysFromToday', 'afterDaysFromToday'].includes(filter.operation.value) ? (
        <NumberInput
          placeholder="No. of days"
          value={selectedNumericValue || ''}
          onChange={e => onChange(getNumericDaysFilterValue(e.target.value))}
          isAllowed={(val: NumberFormatValues) =>
            val.value === '' ||
            (val.floatValue !== undefined &&
              Number.isInteger(val.floatValue) &&
              val.floatValue >= 0)
          }
          className="cps-12 h-100p"
          invisible
        />
      ) : (
        <SingleDateFilter
          value={
            selectedSingeDateValue
              ? hasTime
                ? new Date(`${selectedSingeDateValue}Z`)
                : new Date(selectedSingeDateValue)
              : null
          }
          onChange={(date: DateInputType) => (date ? onChange(getFormattedDate(date)) : null)}
          hasTime={hasTime}
          isClearable={isClearable}
        />
      )}
    </FilterContainer>
  );
};

const SingleDateFilter = ({
  value,
  hasTime = false,
  onChange,
  isClearable = false
}: {
  value: DateInputType;
  hasTime?: boolean;
  onChange: (date: DateInputType) => void;
  isClearable?: boolean;
}) => (
  <SingleDatePicker
    value={value}
    onChange={onChange}
    groupClassName="mb-0 h-100p"
    displayClassName="max-w-100p h-100p single-date"
    isClearable={isClearable}
    hasTime={hasTime}
  />
);

const RangeDateFilter = ({
  from,
  to,
  hasTime = false,
  options,
  onChange,
  isClearable = false
}: {
  from: DateInputType;
  to: DateInputType;
  hasTime?: boolean;
  options?: DateRangeOptionType[];
  onChange: (dates: { from: DateInputType; to: DateInputType }) => void;
  isClearable?: boolean;
}) => (
  <DateRangePicker
    from={from}
    to={to}
    onChange={onChange}
    groupClassName="mb-0 h-100p"
    displayClassName="max-w-100p h-100p text-nowrap text-truncate overflow-hidden"
    isClearable={isClearable}
    hasTime={hasTime}
    options={options || getPastDateRangeOptions()}
  />
);

const FilterContainer = styled.div`
  border: 1px solid ${variables.silver};
  border-top-right-radius: 3px;
  border-bottom-right-radius: 3px;
  height: 100%;
`;

export default DateFilterSelector;
